// Markdown ของคำตอบ chatbot — **แหล่งเดียวของทั้งระบบ**
// ใช้ร่วมกันโดย ChatbotView.vue (หน้าเต็ม) และ ChatbotFab.vue (ปุ่มลอย) ซึ่งเดิมมีฟังก์ชันนี้
// คนละก๊อปปี้ แล้วก็ drift ทันทีที่แก้ข้างเดียว (ตารางขึ้นในหน้าเต็ม แต่ปุ่มลอยยังโชว์ |---|---| ดิบ)
//
// ⚠️ ไม่ใช้ไลบรารี markdown โดยตั้งใจ: escape ก่อน แล้วค่อยแปลงเฉพาะ pattern ที่รู้จัก
// = ไม่มีทาง inject HTML ได้เลย (แนวเดียวกับ InsightView.vue) การเพิ่ม marked/DOMPurify
// แค่เพื่อตารางไม่คุ้มกับพื้นที่ผิวความปลอดภัยที่เพิ่มขึ้น
//
// รองรับ: **ตัวหนา** · ## / ### หัวข้อ · bullet (- * •) · **ตาราง Markdown**

// แถวตาราง = มีตัวคั่น | อย่างน้อย 1 ตัว · **ไม่บังคับว่าต้องมี | หัวท้าย** เพราะ Markdown ยอมให้
// ละได้ (`ชื่อ | ยอด` + `--- | ---`) และโมเดลก็เขียนแบบนั้นเป็นครั้งคราว — ถ้าบังคับ ตารางจะหลุด
// ไปเป็นข้อความดิบทั้งก้อน · ตัวที่กันไม่ให้ข้อความธรรมดาถูกมองเป็นตารางคือ "บรรทัดคั่น" ซึ่งเข้มมาก
// (ทุกช่องต้องเป็นขีดล้วน) ไม่ใช่เงื่อนไขของแถวเอง
// | ที่ไม่ได้ถูก escape ด้วย \ ข้างหน้า (ตัวคั่นช่องจริง) — แยก 2 ตัวเพราะ regex ที่มีธง g
// จำ lastIndex ข้ามการเรียก .test() ทำให้ผลสลับ true/false เมื่อใช้ตัวเดียวกันซ้ำ
const PIPE_SPLIT = /(?<!\\)\|/g;
const hasPipe = (s) => /(?<!\\)\|/.test(s);

const isTableRow = (line) => {
  if (typeof line !== 'string') return false;
  const t = line.trim();
  if (!t) return false;
  // bullet ที่บังเอิญมี | อยู่ข้างใน ไม่ใช่แถวตาราง — กันไม่ให้ลิสต์ใต้ตารางถูกกลืนเข้าไปเป็นแถว
  if (/^[•*]\s/.test(t) || /^-\s/.test(t)) return false;
  return hasPipe(t);
};

// ตัด | หัวท้าย (ถ้ามี) ก่อนค่อย split — ไม่งั้นจะได้ช่องว่างหลอกที่หัวและท้ายทุกแถว
// ⚠️ split ที่ตัวคั่นจริงเท่านั้น: `A \| B` คือช่องเดียวที่มีขีดคั่นอยู่ข้างใน ไม่ใช่สองช่อง
// (ถ้า split ดิบ ๆ ค่าจะเลื่อนคอลัมน์ทั้งแถว) แล้วคืน \| ให้เป็น | ธรรมดาตอนแสดง
const splitTableRow = (line) => line.trim()
  .replace(/^\|/, '').replace(/\|$/, '')
  .split(PIPE_SPLIT)
  .map((c) => c.trim().replace(/\\\|/g, '|'));

// บรรทัดคั่นหัวตาราง: | --- | :---: | ---: |  (ทุกช่องต้องเป็นขีดล้วน)
const isTableDivider = (line) => {
  if (!isTableRow(line)) return false;
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
};

const alignOf = (cell) => (/^:-+:$/.test(cell) ? 'center' : (/-+:$/.test(cell) ? 'right' : 'left'));

export function formatChatMarkdown(text) {
  if (!text) return '';
  const esc = String(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  let html = esc.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/^###\s+(.+)$/gm, '<h5>$1</h5>').replace(/^##\s+(.+)$/gm, '<h4>$1</h4>');
  const lines = html.split('\n');
  const out = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    // ── ```code``` → <pre> ที่เลือก/คัดลอกง่าย (ใช้ตอนบอทลิสต์รหัสแพ็กเกจให้ผู้ใช้ไปคัดลอก)
    // ไม่เจอ ``` ปิด = ถือว่าถึงท้ายข้อความ · เนื้อในถูก escape แล้วตั้งแต่ต้นฟังก์ชัน
    if (/^\s*```/.test(line)) {
      closeList();
      const rest = line.replace(/^\s*```/, '');
      const code = [];
      const closeAt = rest.indexOf('```');
      if (closeAt >= 0) { // ทั้งก้อนอยู่บรรทัดเดียว: ```A, B, C```
        code.push(rest.slice(0, closeAt));
      } else {
        if (rest.trim() && !/^\w+$/.test(rest.trim())) code.push(rest); // มีเนื้อหาต่อท้าย ``` ไม่ใช่แค่ชื่อภาษา
        let j = i + 1;
        while (j < lines.length && !/^\s*```/.test(lines[j])) { code.push(lines[j]); j += 1; }
        i = j;
      }
      out.push(`<pre class="cb-code" style="white-space:pre-wrap;word-break:break-all;user-select:all;margin:6px 0;padding:8px 10px;border-radius:8px;background:rgba(0,0,0,.18);font-size:.85em">${code.join('\n').trim()}</pre>`);
      continue;
    }

    // ── ตาราง: หัวตาราง + บรรทัดคั่น + แถวข้อมูล → <table class="idt"> ตามมาตรฐานกริดของระบบ
    // ก่อนหน้านี้บรรทัดพวกนี้ตกไปเป็น <p> ธรรมดา ผู้ใช้จึงเห็น |---|---| ดิบ ๆ (เจ้าของแจ้ง)
    if (isTableRow(line) && isTableDivider(lines[i + 1])) {
      closeList();
      const header = splitTableRow(line);
      const align = splitTableRow(lines[i + 1]).map(alignOf);
      const body = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j])) { body.push(splitTableRow(lines[j])); j += 1; }
      i = j - 1;
      const th = header.map((c, k) => `<th style="text-align:${align[k] || 'left'}">${c}</th>`).join('');
      // ยึดจำนวนช่องตามหัวตารางเสมอ — แถวที่โมเดลใส่ช่องขาด/เกินต้องไม่ทำให้ตารางเบี้ยว
      const tr = body.map((row) => `<tr>${header.map((_, k) => (
        `<td style="text-align:${align[k] || 'left'}">${row[k] === undefined ? '' : row[k]}</td>`
      )).join('')}</tr>`).join('');
      out.push(`<div class="cb-table-wrap"><table class="idt"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></div>`);
      continue;
    }

    // เส้นคั่น --- / *** — ต้องเช็คก่อน bullet เพราะ "---" ขึ้นต้นด้วย - เหมือนกัน
    // (ก่อนหน้านี้ตกไปเป็น <p>---</p> ผู้ใช้เห็นขีดสามตัวลอย ๆ)
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) { closeList(); out.push('<hr>'); continue; }

    const bullet = /^\s*[•\-*]\s+(.*)$/.exec(line);
    if (bullet) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${bullet[1]}</li>`);
    } else {
      closeList();
      if (line.trim() === '') out.push('<br>'); else out.push(`<p>${line}</p>`);
    }
  }
  closeList();
  return out.join('');
}

// ── ข้อความระหว่างรอคำตอบ (at = วินาทีที่ ms นับจากกดส่ง — สะสม ไม่ใช่ระยะห่างจากขั้นก่อน) ──────
// คำขอเดียวจบใน HTTP รอบเดียว หน้าจอจึงไม่รู้ว่าตอนนั้นบอทเรียก tool ตัวไหนอยู่ ถ้อยคำจึงต้อง
// "จริงเสมอ" ไม่ว่าจะอยู่ขั้นไหน — ห้ามเขียนว่า "กำลังสรุป" ทั้งที่อาจยังดึงข้อมูลอยู่
// (อยากได้สถานะจริงต้องเปลี่ยน endpoint เป็น SSE = เฟสถัดไป)
export const THINKING_STEPS = [
  { text: 'กำลังคิด…', at: 0 },
  { text: 'กำลังค้นข้อมูลจากระบบ…', at: 2500 },
  { text: 'กำลังรวบรวมตัวเลข…', at: 5000 },
  { text: 'คำถามนี้ใช้เวลาสักครู่ กำลังทำอยู่…', at: 9000 },
];
